// Program cost allocation client (/api/chargeback).
//
// Mirrors backend/apis/chargeback.py. Every amount comes back with the basis it
// was derived from — a charge a program office cannot interrogate is a charge
// they will dispute.

import { get, post, AI_TIMEOUT_MS } from "./http";

/** The consumers money can be allocated to, with staff share. */
export const getPrograms = () => get("/chargeback/programs");

/** What each program consumes and what it costs them, plus the rounding residual. */
export const getSummary = (fiscalYear) =>
  get("/chargeback/summary", { query: { fiscal_year: fiscalYear } });

/** One program's itemised bill. */
export const getStatement = (code, fiscalYear) =>
  get(`/chargeback/statement/${encodeURIComponent(code)}`, { query: { fiscal_year: fiscalYear } });

/** The full product-by-program matrix. */
export const getAllocation = (params = {}) =>
  get("/chargeback/allocation", { query: { product_id: params.productId, fiscal_year: params.fiscalYear } });

/** Which program would fund each demand in flight. */
export const getDemandFunding = (fiscalYear) =>
  get("/chargeback/demands", { query: { fiscal_year: fiscalYear } });

/** Rebuild program records and the allocation matrix. */
export const reseed = () => post("/chargeback/seed", {});

/** Idle spend dated by when the contract renews — the only time it can be cut. */
export const getSavingsCalendar = (horizonDays) =>
  get("/chargeback/savings-calendar", { query: { horizon_days: horizonDays } });

/** Every product on cost against utilisation, with an act-now shortlist. */
export const getUtilisationMap = () => get("/chargeback/utilisation-map");

/** A dated, ordered savings plan for one program office. Calls a model, so slow. */
export const getSavingsPlan = (code, question, { refresh = false } = {}) =>
  post(`/chargeback/savings-plan/${encodeURIComponent(code)}`, { question },
       { query: { refresh }, timeout: AI_TIMEOUT_MS });

/** Spend on products carrying an AI capability, against their governance state.
 *  Not consumption metering — see the provenance block on the response. */
export const getAiSpend = (fiscalYear) =>
  get("/chargeback/ai-spend", { query: { fiscal_year: fiscalYear } });

/** Identified savings and what was actually decided about each one. */
export const getSavingsPipeline = (fiscalYear) =>
  get("/chargeback/savings-pipeline", { query: { fiscal_year: fiscalYear } });

/** Record a decision against an identified saving. */
export const recordSavingsDecision = (actionId, body) =>
  post(`/chargeback/savings-action/${actionId}`, body);
